import './styles/AddModuleSelectionModal.scss';

import { useApolloClient, useMutation, useQuery as useGQLQuery } from '@apollo/client';
import { Box, Checkbox, FormControlLabel, Grid, Tooltip, Typography } from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import { Module } from '@thro-types/modules-selections';
import { ShibUser } from '@thro-types/users';

import { Formik, useFormikContext } from 'formik';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

import ADD_MODULE_TO_SELECTION_MUTATION from 'gql/mutations/ModulesService/addSelectionModule.gql';
import AVAILABLE_MODULES from 'gql/queries/ModulesService/getAvailableModulesForSelection.gql';
import QUERY_GROUPINGS from 'gql/queries/ModulesService/getGroupings.gql';
import USER_QUERY from 'gql/queries/currentUser.gql';
import USERS_QUERY from 'gql/queries/getUsers.gql';

import { invalidateModulesQuery } from 'api/hooks/modules';
import { useCoursesQuery } from 'api/hooks/useCoursesQuery';
import { Filter } from 'app/components/Filters';
import GQLGuard from 'app/components/GQLGuard';
import MultistepForm from 'app/components/MultistepForm';
import { NoItems } from 'app/components/RichList/components/NoItems';
import { ModuleItem } from 'app/components/RichList/items/AWPM-FWPM/ModuleItem';
import { TilesOrListView, TypedGridColumns } from 'app/components/TilesOrListView';
import { ModuleWithComplete } from 'app/containers/AWPM-FWPM/Modules/models';
import {
  handleModulesFilterQuery,
  userDataToLecturerOption
} from 'app/containers/AWPM-FWPM/helper';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { SortOption } from 'types/AppTypes';
import { ACLHelper, UserType } from 'utils/ACLHelper';
import { validateForm } from 'utils/FormErrorHelper';

import { SubjectType, UsersQueryArgs } from '../../models';
import { addModuleToSelectionValidationSchema } from './validation';

interface AddModuleToSelectionModalProps {
  moduleSelectionId: string;
  closeHandler: () => void;
  subjectType: SubjectType;
}

const AddModuleStep = ({
  step,
  subjectType,
  id
}: {
  step: number;
  subjectType: SubjectType;
  id: string;
}) => {
  switch (step) {
    case 0:
      return <SelectCourses subjectType={subjectType} id={id} />;
    case 1:
      return <Confirmation subjectType={subjectType} id={id} />;
    default:
      return null;
  }
};

export const AddModuleToSelectionModal: React.FC<AddModuleToSelectionModalProps> = ({
  moduleSelectionId,
  closeHandler,
  subjectType
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState(false);

  const [addModuleToSelection] = useMutation(ADD_MODULE_TO_SELECTION_MUTATION, {
    context: { clientName: 'federation' }
  });

  const steps = [
    t(translations.pages.moduleSelectionsDetail.modulePreview.create.steps.selectModules()),
    t(translations.pages.moduleSelectionsDetail.modulePreview.create.steps.confirmation())
  ];

  const handleSaveClick = async (values: { selectedModules: Set<string> }) => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    try {
      await addModuleToSelection({
        variables: {
          id: moduleSelectionId,
          data: {
            selectionId: [...values.selectedModules].map(module => module.toString())
          }
        }
      });
      invalidateModulesQuery(queryClient);
    } catch (e) {
      dispatch(
        AlertActions.error(
          t(translations.pages.moduleSelectionsDetail.modulePreview.create.alerts.saveFailed())
        )
      );
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
    closeHandler();
  };

  return (
    <Grid item className="moduleModal">
      <Formik
        initialValues={{ selectedModules: new Set<string>() }}
        validationSchema={yup.object().shape(addModuleToSelectionValidationSchema(t))}
        onSubmit={values => {
          handleSaveClick(values);
        }}
        validate={values => {
          return validateForm(
            values,
            yup.object().shape(addModuleToSelectionValidationSchema(t)),
            dispatch
          );
        }}
      >
        <MultistepForm
          submitting={submitting}
          steps={steps}
          onCancel={closeHandler}
          isStepperClickable
        >
          {step => (
            <Grid container>
              <AddModuleStep step={step} subjectType={subjectType} id={moduleSelectionId} />
            </Grid>
          )}
        </MultistepForm>
      </Formik>
    </Grid>
  );
};

interface SelectAllCheckboxProps {
  data: any;
  onChange: (checked: boolean, modules: Module[]) => void;
}
const SelectAllCheckbox = ({ data, onChange }: SelectAllCheckboxProps) => {
  const { values } = useFormikContext<{ selectedModules: Set<string> }>();
  const { t } = useTranslation();

  return (
    <FormControlLabel
      label={t(translations.pages.moduleSelectionsDetail.modulePreview.create.selectAllModules())}
      onClick={event => event.stopPropagation()}
      onFocus={event => event.stopPropagation()}
      control={
        <Checkbox
          checked={
            values.selectedModules?.size === data?.getAvailableModulesForSelection?.length &&
            data?.getAvailableModulesForSelection?.length !== 0
          }
          onClick={e => e.stopPropagation()}
          onChange={e => onChange(e.target.checked, data?.getAvailableModulesForSelection)}
        />
      }
      style={{ color: 'black' }}
    />
  );
};

interface Props {
  subjectType: SubjectType;
  id?: string;
}

const SelectCourses = (props: Props) => {
  const groupingInputState = useState([]);
  const titleInputState = useState('');
  const completionInputState = useState('');
  const coursesInputState = useState([]);

  const { t } = useTranslation();

  const client = useApolloClient();
  const { currentUser } = client.readQuery({ query: USER_QUERY });
  const aclHelper = new ACLHelper(currentUser);

  const { data: coursesData } = useCoursesQuery();

  const shouldGetSelectionAdminCourses =
    !aclHelper.isSuperUser() && props.subjectType === SubjectType.Fwpm;

  const courses = shouldGetSelectionAdminCourses
    ? aclHelper.getSelectionAdminCourses(coursesData)
    : undefined;

  const getAvailableModules = useGQLQuery<any, any>(AVAILABLE_MODULES, {
    context: { clientName: 'federation' },
    fetchPolicy: 'cache-and-network',
    variables: {
      selectionId: props.id,
      nameSort: 'asc',
      subjectType: props.subjectType,
      courses: courses ? courses?.map(course => course.id) : undefined
    }
  });
  const { refetch } = getAvailableModules;

  const handleFilterChange = variables => {
    const filterVariables = handleModulesFilterQuery(variables);

    if (filterVariables.courses !== undefined && filterVariables.courses.length === 0) {
      filterVariables.courses = courses ? courses?.map(course => course.id) : undefined;
    }

    refetch({ courses: undefined, ...filterVariables });
  };

  const usersQuery = useGQLQuery<any, UsersQueryArgs>(USERS_QUERY, {
    variables: {
      type: UserType.SHIBBOLETH,
      entitlements: [
        'IDM_Professoren_Gesamt',
        'IDM_Lehrbeauftragte_Gesamt',
        'IDM_Mitarbeiter_Gesamt'
      ],
      limit: 0
    },
    context: { clientName: 'federation' }
  });

  const { values, setFieldValue } = useFormikContext<{
    selectedModules: Set<number>;
  }>();

  const handleSelect = (moduleId: number) => {
    let selectedModules: Set<number> = new Set<number>(values.selectedModules);
    if (values.selectedModules.has(moduleId)) {
      selectedModules.delete(moduleId);
    } else {
      selectedModules.add(moduleId);
    }
    setFieldValue('selectedModules', selectedModules);
  };

  const handleSelectAll = (checked: boolean, modules: Module[]) => {
    let selectedModules: Set<number> = new Set();
    if (checked) {
      selectedModules = new Set(modules.map(module => module.id));
    }
    setFieldValue('selectedModules', selectedModules);
  };

  const onSelectionChange = (selection: GridRowSelectionModel) => {
    let selectedModules: Set<string> = new Set(selection.map(id => id as string));
    setFieldValue('selectedModules', selectedModules);
  };

  const getGroupings = useGQLQuery<any, any>(QUERY_GROUPINGS, {
    context: { clientName: 'federation' }
  });

  const columns = useMemo((): TypedGridColumns<ModuleWithComplete & { lecturer: ShibUser }> => {
    return [
      {
        flex: 3,
        minWidth: 200,
        headerName: t(translations.pages.modules.create.modulePreview.title()),
        field: 'name',
        renderCell: params => (
          <Tooltip title={params.row.name}>
            <span>{params.row.name}</span>
          </Tooltip>
        )
      },
      {
        flex: 2,
        minWidth: 200,
        headerName: t(translations.pages.modules.lecturer()),
        field: 'lecturer',
        renderCell: params =>
          !!params.row.lecturer ? (
            <Tooltip title={userDataToLecturerOption(params.row.lecturer, t).label}>
              <span>{userDataToLecturerOption(params.row.lecturer, t).label}</span>
            </Tooltip>
          ) : (
            <></>
          )
      },
      {
        flex: 2,
        minWidth: 200,
        headerName: t(translations.pages.modules.grouping()),
        field: 'groupingId',
        valueGetter: (value, row) => row.grouping?.name,
        renderCell: params => (
          <Tooltip title={params.row.grouping?.name}>
            <span>{params.row.grouping?.name}</span>
          </Tooltip>
        )
      },
      {
        flex: 1,
        minWidth: 200,
        headerName: t(translations.pages.modules.seatsAvailable()),
        field: 'maxParticipants',
        renderCell: params => (
          <Tooltip title={params.row.maxParticipants}>
            <span className="module__footer__seats">{params.row.maxParticipants}</span>
          </Tooltip>
        )
      },
      {
        flex: 1,
        minWidth: 200,
        headerName: t(translations.pages.modules.takesPlace()),
        field: 'isTakingPlace',
        renderCell: params => (
          <Box className={`module__tookPlace module__tookPlace-${params.row.isTakingPlace}`}>
            {params.row.isTakingPlace
              ? t(translations.pages.modules.takesPlace())
              : t(translations.pages.modules.isNotTakingPlace())}
          </Box>
        )
      }
    ];
  }, [t]);

  const richlistFilter = (groupings: any): Filter[] => [
    {
      field: 'name',
      label: t(translations.pages.moduleSelectionsDetail.filter.search()),
      stateHandler: titleInputState
    },
    {
      field: 'completionState',
      label: t(translations.pages.moduleSelectionsDetail.filter.completed()),
      select: {
        options: [
          { label: '---', name: null },
          { label: 'Vollständig', name: 'completed' },
          { label: 'Entwurf', name: 'draft' }
        ],
        stateHandler: completionInputState
      }
    },
    {
      field: 'courses',
      label: t(translations.pages.moduleSelectionsDetail.filter.courseOfStudy()),
      multiselect: {
        options: courses
          ? courses.map(course => ({ label: course.name, name: course.id }))
          : coursesData.map(course => ({
              label: course.name,
              name: course.id
            })),
        stateHandler: coursesInputState,
        customStyle: { width: '200px' }
      }
    },
    {
      field: 'groupings',
      label: t(translations.pages.modules.grouping()),
      multiselect: {
        options: groupings.map(grouping => {
          return { label: grouping.name, name: parseInt(grouping.id, 10) };
        }),
        stateHandler: groupingInputState,
        customStyle: { width: '200px' }
      },
      hide: props.subjectType === 'FWPM'
    }
  ];

  const sortOptions: SortOption[] = [
    {
      label: t(translations.pages.moduleSelectionsDetail.sort.nameAsc()),
      value: { nameSort: 'asc' }
    },
    {
      label: t(translations.pages.moduleSelectionsDetail.sort.nameDesc()),
      value: { nameSort: 'desc' }
    }
  ];

  const onSortChange = sortings => {
    refetch(sortings);
  };

  return (
    <GQLGuard query={[getAvailableModules, usersQuery, getGroupings]}>
      {([modulesData, lecturerData, groupingsData]) => (
        <Box flexGrow={1}>
          <Grid container>
            <Grid item xs={6}>
              <Typography component="h2">
                {t(translations.pages.moduleSelectionsDetail.modulePreview.create.selectModules())}
              </Typography>
            </Grid>
          </Grid>
          <Grid container className="add-module-selection" style={{ width: '100%' }}>
            {modulesData?.getAvailableModulesForSelection ? (
              <TilesOrListView
                data={modulesData.getAvailableModulesForSelection.map(module => ({
                  ...module,
                  lecturer: lecturerData.users.find(
                    lecturer => parseInt(lecturer.id, 10) === module.moduleOfficer
                  )
                }))}
                filters={richlistFilter(groupingsData.getGroupings)}
                onFilterChange={handleFilterChange}
                sortOptions={sortOptions}
                onSortChange={onSortChange}
                controlElements={
                  <SelectAllCheckbox data={modulesData} onChange={handleSelectAll} />
                }
                hasCheckboxSelection
                height={'40vh'}
                selectionModal={Array.from(values.selectedModules)}
                onSelectionChange={rowIds => {
                  onSelectionChange(rowIds);
                }}
                renderTile={module => (
                  <Grid
                    key={module.id}
                    item
                    alignContent="center"
                    justifyContent="center"
                    className={values.selectedModules.has(module.id) ? 'module-box-selected' : ''}
                  >
                    <ModuleItem
                      key={module.id}
                      deactivateButtons
                      deactiveLink
                      checked={values.selectedModules.has(module.id)}
                      module={module}
                      onClick={() => handleSelect(module.id)}
                      subjectType={props.subjectType}
                    />
                  </Grid>
                )}
                columns={columns}
                columnVisibilityModel={{ grouping: props.subjectType !== SubjectType.Fwpm }}
              />
            ) : (
              <NoItems
                message={t(
                  translations.pages.moduleSelectionsDetail.modulePreview.create.noItems()
                )}
              />
            )}
          </Grid>
        </Box>
      )}
    </GQLGuard>
  );
};

const Confirmation = (props: Props) => {
  const { values } = useFormikContext<{ selectedModules: Set<string> }>();
  const { t } = useTranslation();

  const getAvailableModules = useGQLQuery<any, any>(AVAILABLE_MODULES, {
    context: { clientName: 'federation' },
    fetchPolicy: 'cache-and-network',
    variables: { selectionId: props.id, subjectType: props.subjectType, nameSort: 'asc' }
  });

  return (
    <GQLGuard query={getAvailableModules}>
      {data => (
        <Box className="add-module-selection__confirmation" flexGrow={1}>
          <Typography component="h2">
            {t(translations.pages.moduleSelectionsDetail.modulePreview.create.modulePreferences())}
          </Typography>
          {values.selectedModules.size > 0 ? (
            <Grid container justifyContent="flex-start" spacing={1}>
              {Array.from(values.selectedModules).map(moduleId => (
                <Grid
                  xs={12}
                  md={4}
                  item
                  alignContent="center"
                  justifyContent="center"
                  key={moduleId}
                >
                  <ModuleItem
                    key={moduleId}
                    module={data?.getAvailableModulesForSelection?.find(
                      module => module.id === moduleId
                    )}
                    deactivateButtons
                    deactiveLink
                    subjectType={props.subjectType}
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <NoItems
              message={t(
                translations.pages.moduleSelectionsDetail.modulePreview.create.noItemsSelected()
              )}
            />
          )}
        </Box>
      )}
    </GQLGuard>
  );
};
